/* Lynqu CTA uses rel="noopener" (not "noreferrer") so lynqu.com receives the
   referrer for attributed referral traffic + dofollow SEO value. */
/* eslint-disable react/jsx-no-target-blank */
import { useCallback, useEffect, useRef, useState } from "react";
import "../../styles/lynqu.css";
import "../../styles/flatrent.css";
import MockIphone from "../../assets/mock_iphone2.webp";
import MockAndroid from "../../assets/mock_android2.webp";
import LynquPhone from "../../assets/lynqu/liara-dark-iphone.webp";
import FrbPhone from "../../assets/frb/frb-flats-iphone.webp";
import { useReducedMotion } from "../hooks/useReducedMotion";
import { spring, project, rubberband, clamp, SPRING } from "../utils/motion";

const SLIDES = 3;
const AUTOPLAY_MS = 6500;
const LYNQU_URL = "https://lynqu.com";
const FRB_URL = "https://flatrentbudapest.com";
// Same destination as the nav's BOOK NOW (Home.jsx onContactUsClicked) — the
// mobile hero needs its own above-the-fold CTA because BOOK NOW lives inside
// the closed hamburger drawer on phones.
const BOOK_URL = "https://lynqu.com/book/maamjadi";
const FOCUS_RING =
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/80 focus-visible:ring-offset-2 focus-visible:ring-offset-[#292929]";

/* Before we commit to "this is a horizontal swipe" we let the pointer travel a
 * little and watch which axis wins, so a vertical scroll that drifts sideways
 * never steals the page. */
const AXIS_LOCK_PX = 10;
/* A flick this fast counts as momentum, and only then does the landing spring
 * get any bounce — overshoot on a slide you nudged feels wrong; overshoot on
 * one you threw feels right. */
const FLICK_VELOCITY = 320;

/* Hidden slides get `inert` so their links/buttons leave the tab order. */
function Slide({ active, index, total, children }) {
  const ref = useRef(null);
  useEffect(() => {
    if (ref.current) ref.current.inert = !active;
  }, [active]);
  return (
    <div
      ref={ref}
      role="group"
      aria-roledescription="slide"
      aria-label={`${index + 1} of ${total}`}
      aria-hidden={!active}
      className="relative h-full shrink-0 overflow-hidden"
      style={{ width: `${100 / total}%` }}>
      {children}
    </div>
  );
}

export default function Digital(props) {
  const reducedMotion = useReducedMotion();
  const [current, setCurrent] = useState(0);
  /* Two different things, and merging them is what made the hero look like it
   * shipped paused: `userPaused` is a choice the pause button owns and reports,
   * `focusHeld` is a transient hold while a keyboard user is inside the
   * carousel. Hovering is deliberately NOT one of them — the hero fills the
   * viewport, so there is nowhere to put a desktop cursor that is "outside" it,
   * and a hover pause on a full-bleed section is indistinguishable from
   * autoplay never running. */
  const [userPaused, setUserPaused] = useState(false);
  const [focusHeld, setFocusHeld] = useState(false);
  const [hoverHeld, setHoverHeld] = useState(false);
  const [dragging, setDragging] = useState(false);
  /* Only user-driven changes are announced. A live region that fires every
   * autoplay tick interrupts a screen reader every few seconds for no reason. */
  const [announce, setAnnounce] = useState("");

  const sectionRef = useRef(null);
  const trackRef = useRef(null);
  const widthRef = useRef(0);
  /* The track's live x lives in a ref and is written straight to the DOM: a
   * drag must track the finger frame for frame, and routing every pointermove
   * through React state would put a render between the two. */
  const xRef = useRef(0);
  const springRef = useRef(null);
  const gesture = useRef(null);
  const currentRef = useRef(0);

  const setX = useCallback((next) => {
    xRef.current = next;
    if (trackRef.current) {
      trackRef.current.style.transform = `translate3d(${next}px, 0, 0)`;
    }
  }, []);

  const stopSpring = useCallback(() => {
    if (!springRef.current) return 0;
    const { velocity } = springRef.current.stop();
    springRef.current = null;
    return velocity;
  }, []);

  /* Always animate FROM the value on screen, never from the logical one. */
  const settleTo = useCallback(
    (index, velocity = 0) => {
      const w = widthRef.current;
      const target = -index * w;
      currentRef.current = index;
      setCurrent(index);

      if (reducedMotion || !w) {
        stopSpring();
        setX(target);
        return;
      }

      const flicked = Math.abs(velocity) > FLICK_VELOCITY;
      const cfg = flicked ? SPRING.momentum : SPRING.move;

      if (springRef.current && !springRef.current.finished) {
        // Redirect the running spring so its velocity carries through the
        // reversal instead of being cut off and restarted.
        springRef.current.retarget(target);
        return;
      }
      stopSpring();
      springRef.current = spring({
        from: xRef.current,
        to: target,
        velocity,
        damping: cfg.damping,
        response: cfg.response,
        onUpdate: (v) => setX(v),
        onComplete: (v) => {
          setX(v);
          springRef.current = null;
        },
      });
    },
    [reducedMotion, setX, stopSpring]
  );

  const goTo = useCallback(
    (i, { announceIt = true } = {}) => {
      const next = ((i % SLIDES) + SLIDES) % SLIDES;
      if (announceIt) setAnnounce(`Slide ${next + 1} of ${SLIDES}`);
      settleTo(next);
    },
    [settleTo]
  );
  const next = useCallback(() => goTo(currentRef.current + 1), [goTo]);
  const prev = useCallback(() => goTo(currentRef.current - 1), [goTo]);

  /* ---- measure ---- */
  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return undefined;
    const measure = () => {
      const w = el.clientWidth;
      if (!w || w === widthRef.current) return;
      widthRef.current = w;
      stopSpring();
      setX(-currentRef.current * w);
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [setX, stopSpring]);

  useEffect(() => () => stopSpring(), [stopSpring]);

  /* Spread onto the hero's CONTROLS — the CTAs, the edge arrows, the slide
   * rail. Not the section: the hero fills the viewport, so a hover pause on it
   * has no "outside" for a desktop cursor to sit in and reads as autoplay
   * never running. On a control it means what it should — you are reaching for
   * this, so nothing moves out from under you. */
  const holdOnHover = {
    onMouseEnter: () => setHoverHeld(true),
    onMouseLeave: () => setHoverHeld(false),
  };

  /* ---- autoplay ---- */
  const autoplayHeld = userPaused || focusHeld || hoverHeld || dragging;
  useEffect(() => {
    if (autoplayHeld || reducedMotion) return undefined;
    const id = setTimeout(() => goTo(currentRef.current + 1, { announceIt: false }), AUTOPLAY_MS);
    return () => clearTimeout(id);
  }, [autoplayHeld, reducedMotion, current, goTo]);

  /* ---- pointer: 1:1 tracking, axis lock, rubber-band, momentum ---- */
  const onPointerDown = (e) => {
    if (e.pointerType === "mouse" && e.button !== 0) return;
    // Grab whatever is on screen right now, mid-flight included.
    const carried = stopSpring();
    gesture.current = {
      id: e.pointerId,
      startX: e.clientX,
      startY: e.clientY,
      originX: xRef.current,
      axis: null,
      carried,
      history: [{ x: e.clientX, t: performance.now() }],
    };
  };

  const onPointerMove = (e) => {
    const g = gesture.current;
    if (!g || e.pointerId !== g.id) return;
    const dx = e.clientX - g.startX;
    const dy = e.clientY - g.startY;

    if (!g.axis) {
      if (Math.abs(dx) < AXIS_LOCK_PX && Math.abs(dy) < AXIS_LOCK_PX) return;
      g.axis = Math.abs(dx) > Math.abs(dy) ? "x" : "y";
      if (g.axis === "y") {
        // The page owns this gesture. Let go cleanly.
        gesture.current = null;
        return;
      }
      e.currentTarget.setPointerCapture(g.id);
      setDragging(true);
    }

    g.history.push({ x: e.clientX, t: performance.now() });
    if (g.history.length > 6) g.history.shift();

    const w = widthRef.current;
    const min = -(SLIDES - 1) * w;
    let nextX = g.originX + dx;
    // Past either end the track still follows, with rising resistance, so the
    // edge reads as "nothing more here" rather than "frozen".
    if (nextX > 0) nextX = rubberband(nextX, w);
    else if (nextX < min) nextX = min + rubberband(nextX - min, w);
    setX(nextX);
  };

  const endGesture = (e) => {
    const g = gesture.current;
    if (!g || (e && e.pointerId !== g.id)) return;
    gesture.current = null;
    if (g.axis !== "x") return;
    setDragging(false);

    const w = widthRef.current || 1;
    const now = performance.now();
    const first = g.history[0];
    const last = g.history[g.history.length - 1];
    const dt = Math.max(1, (last.t - first.t) || now - first.t);
    const velocity = ((last.x - first.x) / dt) * 1000; // px/s

    // Land where the gesture was GOING, not where the finger stopped.
    const projected = xRef.current + project(velocity);
    const index = clamp(Math.round(-projected / w), 0, SLIDES - 1);
    setAnnounce(`Slide ${index + 1} of ${SLIDES}`);
    settleTo(index, velocity);
  };

  const onKeyDown = (e) => {
    if (e.key === "ArrowRight") {
      e.preventDefault();
      next();
    } else if (e.key === "ArrowLeft") {
      e.preventDefault();
      prev();
    }
  };

  const handleThereIsMore = () => {
    document.getElementById("services")?.scrollIntoView({ behavior: "smooth" });
  };

  /* Scroll cue: fades in after a beat, fades out over the first 200px. */
  const [cueOpacity, setCueOpacity] = useState(0);
  useEffect(() => {
    const t = setTimeout(() => setCueOpacity(1), 1400);
    const onScroll = () => setCueOpacity(Math.max(0, 1 - window.scrollY / 200));
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      clearTimeout(t);
      window.removeEventListener("scroll", onScroll);
    };
  }, []);

  return (
    <section
      id="digital-world"
      ref={(node) => {
        sectionRef.current = node;
        if (typeof props.sectionRef === "function") props.sectionRef(node);
        else if (props.sectionRef) props.sectionRef.current = node;
      }}
      /* Height is CSS, not a measured inline style. svh tracks the mobile
         browser's collapsing chrome without a resize listener, and without
         baking the prerender viewport's pixel height into the HTML. */
      /* Selection is suppressed only WHILE dragging: a horizontal gesture must
         not paint a text selection, but the headline and the paragraph are
         real copy and stay selectable at rest. */
      className={`DigitalSection relative w-full min-h-[100svh] nav-sm:min-h-[min(100svh,52vw+25rem)] overflow-hidden bg-colorNavLink ${dragging ? "no-select" : ""}`}
      aria-roledescription="carousel"
      aria-label="Gravisun highlights"
      /* Vertical scrolling stays with the browser; we only claim the X axis. */
      style={{ touchAction: "pan-y" }}
      onFocusCapture={() => setFocusHeld(true)}
      onBlurCapture={() => setFocusHeld(false)}
      onKeyDown={onKeyDown}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={endGesture}
      onPointerCancel={endGesture}>
      <div
        ref={trackRef}
        className="absolute inset-y-0 left-0 flex h-full will-change-transform"
        style={{ width: `${SLIDES * 100}%`, transform: "translate3d(0px, 0, 0)" }}>
        {/* ============ SLIDE 1 — Agency ============ */}
        <Slide active={current === 0} index={0} total={SLIDES}>
          {/* ONE background stack for every breakpoint. The mobile and desktop
              variants used to be duplicated DOM, so all four device mockups
              downloaded on every device to show two. */}
          <div className="absolute inset-0 overflow-hidden">
            <div className="absolute inset-0 z-0 flex items-center justify-center blur-[15px] saturate-[155%] brightness-[0.65] nav-sm:brightness-[0.5]">
              <div className="flex h-full shrink-0 items-center justify-end rotate-[-5deg]">
                <img
                  src={MockAndroid}
                  alt=""
                  aria-hidden="true"
                  width={440}
                  height={1002}
                  fetchpriority="high"
                  className="object-contain max-w-none max-h-[78vh] nav-sm:max-w-full nav-sm:max-h-[86vh] c-xl:max-h-[840px]"
                  loading="eager"
                  decoding="async"
                />
              </div>
              <div className="flex h-full shrink-0 items-center justify-start rotate-[4deg]">
                <img
                  src={MockIphone}
                  alt=""
                  aria-hidden="true"
                  width={480}
                  height={999}
                  fetchpriority="high"
                  className="object-contain max-w-none max-h-[78vh] nav-sm:max-w-full nav-sm:max-h-[86vh] c-xl:max-h-[840px]"
                  loading="eager"
                  decoding="async"
                />
              </div>
            </div>
            {/* Mica/Acrylic material over the blurred, dimmed mockups. */}
            <div className="absolute inset-0 z-[5] bg-colorNavLink/35 nav-sm:bg-colorNavLink/45 hero-acrylic pointer-events-none" />
            {/* Directional shade, anchored to the sun (see .hero-shade). */}
            <div className="absolute inset-0 z-[6] pointer-events-none hero-shade" />
            <div className="absolute bg-colorSun sun hero-sun z-10" />
          </div>

          <div className="absolute inset-0 z-20 flex flex-col items-center justify-start pt-[20vh] nav-sm:pt-[25vh] c-2lg:pt-[27vh] text-center px-[24px] nav-sm:px-[40px]">
            <div className="inline-flex items-center gap-[9px] rounded-full border border-[#E1551140] bg-[#E155113d] pl-[10px] pr-[16px] py-[6px] mb-[22px] nav-sm:mb-[26px]">
              <span className="relative flex h-[7px] w-[7px]">
                <span className="absolute inline-flex h-full w-full rounded-full bg-[#E15511] opacity-60 animate-ping motion-reduce:animate-none" />
                <span className="relative inline-flex h-[7px] w-[7px] rounded-full bg-[#E15511]" />
              </span>
              <span className="text-11 nav-sm:text-12 font-semibold tracking-[0.18em] text-[#F0976B] uppercase">
                Full-stack product studio
              </span>
            </div>
            {/* Size is fluid CSS rather than a JS-measured breakpoint prop, so
                the prerendered HTML is right on every device. */}
            <h1
              className="text-36 md:text-50 nav-sm:text-56 c-lg:text-64 c-xl:text-78 leading-[1.05] text-white font-bold"
              style={{ textShadow: "0 2px 30px rgba(8,8,10,0.65)" }}>
              Digitalizing the world
            </h1>
            <p
              className="text-15 md:text-18 c-xl:text-20 leading-[1.55] mt-[16px] nav-sm:mt-[20px] max-w-[460px] nav-sm:max-w-[560px] text-[#E6E3EE]"
              style={{ textShadow: "0 1px 18px rgba(8,8,10,0.6)" }}>
              We design and build complete digital products — across mobile, web,
              backend, and cloud — engineered for performance and real growth.
            </p>
            <a
              href={BOOK_URL}
              target="_blank"
              rel="noopener"
              {...holdOnHover}
              className={`nav-sm:hidden group mt-[26px] inline-flex items-center justify-center gap-[9px] rounded-full bg-[#CB4909] hover:bg-[#B33D06] text-white font-semibold text-15 px-[26px] py-[14px] min-h-[48px] transition-[background-color,transform] duration-200 ease-out hover:-translate-y-[2px] active:translate-y-0 shadow-[0_14px_30px_-10px_rgba(225,85,17,0.6)] ${FOCUS_RING}`}>
              Book a call
              <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className="transition-transform duration-300 group-hover:translate-x-[3px]">
                <path d="M5 12h14" />
                <path d="m13 6 6 6-6 6" />
              </svg>
            </a>
            <button
              type="button"
              onClick={handleThereIsMore}
              {...holdOnHover}
              className={`mt-[18px] nav-sm:mt-[30px] min-h-[44px] text-15 md:text-16 c-xl:text-18 text-white/85 hover:text-white ${FOCUS_RING} rounded transition-colors duration-300 cursor-pointer inline-flex items-center gap-2 group`}>
              There is more
              <svg aria-hidden="true" viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="inline-block transition-transform duration-300 group-hover:translate-y-1">
                <path d="m6 9 6 6 6-6" />
              </svg>
            </button>
          </div>
        </Slide>

        {/* ============ SLIDE 2 — Lynqu ============ */}
        <Slide active={current === 1} index={1} total={SLIDES}>
          <div className="absolute inset-0 lynqu-mesh pointer-events-none" />
          <div className="w-full h-full max-w-[1200px] mx-auto nav-sm:flex justify-between items-center px-[24px] c-lg:px-[72px] c-xl:px-[96px] gap-[24px]">
            <div className="w-full nav-sm:w-1/2 flex flex-col justify-center items-center text-center nav-sm:items-start nav-sm:text-left s1-margin-top nav-sm:mt-0 z-20">
              <h2 className="text-white font-bold text-42 md:text-56 c-xl:text-64 leading-[1.04]">
                Networking, captured.
                <br />
                <span className="text-[#8996FF]">Never lost.</span>
              </h2>

              <p className="mt-[18px] max-w-[460px] text-15 md:text-18 c-xl:text-20 leading-[1.5] text-[#D0CCDB]">
                Meet <span className="text-white font-semibold">Lynqu</span> — the
                networking platform we built and ship to the world.
              </p>

              <a href={LYNQU_URL} target="_blank" rel="noopener" {...holdOnHover}
                className={`lynqu-cta ${FOCUS_RING} group mt-[26px] inline-flex items-center gap-[9px] rounded-full px-[26px] py-[14px] min-h-[48px] text-15 c-lg:text-16 font-semibold text-white w-fit`}>
                Explore Lynqu
                <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className="transition-transform duration-300 group-hover:translate-x-[3px] group-hover:-translate-y-[3px]">
                  <path d="M7 17 17 7" />
                  <path d="M7 7h10v10" />
                </svg>
              </a>
            </div>

            <div className="w-full nav-sm:w-1/2 flex items-center justify-center relative z-10 pt-[12px] nav-sm:pt-0 pb-[32vw] nav-sm:pb-0">
              <div className="pointer-events-none absolute w-[70%] aspect-square rounded-full lynqu-device-glow lynqu-glow-pulse" />
              <img src={LynquPhone} alt="A Lynqu profile card on iPhone" width={474} height={1044}
                className="lynqu-float relative z-10 w-[180px] md:w-[300px] c-lg:w-[330px] c-xl:w-[360px] max-w-full drop-shadow-[0_40px_80px_rgba(0,0,0,0.55)]"
                loading="lazy" draggable="false" />
            </div>
          </div>
        </Slide>

        {/* ============ SLIDE 3 — Flat Rent Budapest ============ */}
        <Slide active={current === 2} index={2} total={SLIDES}>
          <div className="absolute inset-0 frb-mesh-dark pointer-events-none" />
          <div className="relative w-full h-full max-w-[1200px] mx-auto nav-sm:flex justify-between items-center px-[24px] c-lg:px-[72px] c-xl:px-[96px] gap-[24px]">
            <div className="w-full nav-sm:w-1/2 flex flex-col justify-center items-center text-center nav-sm:items-start nav-sm:text-left s1-margin-top nav-sm:mt-0 z-20">
              <h2 className="text-white font-bold text-42 md:text-56 c-xl:text-64 leading-[1.04]">
                Homes in Budapest,
                <br />
                <span className="text-[#8fd6d1]">owner to renter.</span>
              </h2>

              <p className="mt-[18px] max-w-[460px] text-15 md:text-18 c-xl:text-20 leading-[1.5] text-[#D0CCDB]">
                Meet <span className="text-white font-semibold">Flat Rent Budapest</span> — the
                free, owner-direct home marketplace we built for our own city.
              </p>

              <a href={FRB_URL} target="_blank" rel="noopener" {...holdOnHover}
                className={`frb-cta ${FOCUS_RING} group mt-[26px] inline-flex items-center gap-[9px] rounded-full px-[26px] py-[14px] min-h-[48px] text-15 c-lg:text-16 font-semibold text-white w-fit`}>
                Explore Flat Rent Budapest
                <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className="transition-transform duration-300 group-hover:translate-x-[3px] group-hover:-translate-y-[3px]">
                  <path d="M7 17 17 7" />
                  <path d="M7 7h10v10" />
                </svg>
              </a>
            </div>

            <div className="w-full nav-sm:w-1/2 flex items-center justify-center relative z-10 pt-[12px] nav-sm:pt-0 pb-[32vw] nav-sm:pb-0">
              <div className="pointer-events-none absolute w-[70%] aspect-square rounded-full frb-device-glow lynqu-glow-pulse" />
              <div className="lynqu-float relative z-10 w-[180px] md:w-[260px] c-lg:w-[280px] c-xl:w-[300px] max-w-full rounded-[34px] md:rounded-[44px] p-[8px] md:p-[11px] bg-[#1b1b1f] shadow-[0_40px_80px_rgba(0,0,0,0.55)]">
                <img src={FrbPhone} alt="Flat Rent Budapest listings on a phone" width={780} height={1688}
                  className="block w-full h-auto rounded-[27px] md:rounded-[34px]" loading="lazy" draggable="false" />
              </div>
            </div>
          </div>
        </Slide>
      </div>

      {/* ============ Scroll cue ============ */}
      <div
        className="pointer-events-none absolute bottom-[18px] left-1/2 -translate-x-1/2 z-30 hidden nav-sm:flex flex-col items-center gap-[8px] transition-opacity duration-300"
        style={{ opacity: cueOpacity }}
        aria-hidden="true">
        <span className="text-11 text-white/60 uppercase tracking-[0.24em] font-medium">Scroll</span>
        <span className="hero-scroll-line block w-[1px] h-[40px]" />
      </div>

      {/* ============ Edge prev/next (desktop) ============ */}
      <button
        type="button"
        onClick={prev}
        {...holdOnHover}
        aria-label="Previous slide"
        className={`hidden nav-sm:grid place-items-center absolute left-[16px] c-lg:left-[28px] top-1/2 -translate-y-1/2 z-40 w-[46px] h-[46px] rounded-full bg-transparent hover:bg-white/[0.16] border border-transparent hover:border-white/15 text-white/70 hover:text-white transition-[background-color,border-color,color] duration-200 cursor-pointer ${FOCUS_RING}`}>
        <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="m15 18-6-6 6-6" />
        </svg>
      </button>
      <button
        type="button"
        onClick={next}
        {...holdOnHover}
        aria-label="Next slide"
        className={`hidden nav-sm:grid place-items-center absolute right-[16px] c-lg:right-[28px] top-1/2 -translate-y-1/2 z-40 w-[46px] h-[46px] rounded-full bg-transparent hover:bg-white/[0.16] border border-transparent hover:border-white/15 text-white/70 hover:text-white transition-[background-color,border-color,color] duration-200 cursor-pointer ${FOCUS_RING}`}>
        <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="m9 18 6-6-6-6" />
        </svg>
      </button>

      {/* ============ Slide controls ============ */}
      <div {...holdOnHover}
        className="absolute bottom-[18px] right-[20px] nav-sm:right-[40px] z-40 flex items-center gap-[10px]">
        <div className="flex items-center gap-[6px]">
          {Array.from({ length: SLIDES }).map((_, i) => {
            const isActive = i === current;
            return (
              <button key={i} type="button" onClick={() => goTo(i)}
                aria-label={`Go to slide ${i + 1}`} aria-current={isActive}
                className={`group relative grid place-items-center h-[32px] min-w-[32px] ${FOCUS_RING} rounded-full cursor-pointer`}>
                <span
                  className={`relative block h-[6px] rounded-full overflow-hidden transition-[width,background-color] duration-300 ${
                    isActive ? "w-[30px] bg-white/25" : "w-[6px] bg-white/30 group-hover:bg-white/55"
                  }`}>
                  {isActive &&
                    (reducedMotion ? (
                      <span className="absolute inset-0 rounded-full bg-white/90" />
                    ) : (
                      <span
                        key={current}
                        className="absolute inset-y-0 left-0 w-full rounded-full bg-white/90 origin-left hero-progress-fill"
                        style={{
                          animationDuration: `${AUTOPLAY_MS}ms`,
                          animationPlayState: autoplayHeld ? "paused" : "running",
                        }}
                      />
                    ))}
                </span>
              </button>
            );
          })}
        </div>
        {!reducedMotion && (
          <button type="button" onClick={() => setUserPaused((p) => !p)}
            aria-label={userPaused ? "Play slideshow" : "Pause slideshow"} aria-pressed={userPaused}
            className={`grid place-items-center w-[32px] h-[32px] rounded-full text-white/55 hover:text-white/90 ${FOCUS_RING} transition-colors duration-200 cursor-pointer`}>
            {userPaused ? (
              <svg viewBox="0 0 24 24" width="13" height="13" fill="currentColor" aria-hidden="true"><path d="M8 5v14l11-7z" /></svg>
            ) : (
              <svg viewBox="0 0 24 24" width="13" height="13" fill="currentColor" aria-hidden="true"><path d="M6 5h4v14H6zM14 5h4v14h-4z" /></svg>
            )}
          </button>
        )}
      </div>

      {/* Announces USER-driven changes only — an autoplay tick every few
          seconds would interrupt a screen reader for nothing. */}
      <p className="sr-only" aria-live="polite">{announce}</p>
    </section>
  );
}
